import UserExam from './user_exam'
import Question, { GroupQuestion, ExamTestQuestion } from './question'
import Image from './image'
import Skill from './skill'
import User from './user'
import Course from './course'
import { differenceInSeconds } from 'date-fns'
import { forEach, filter, findIndex, uniq, maxBy } from 'lodash'
import ExamEvaluation from './exam_evaluation'

export class AnswerFlowQuestion {
  questionId: number
  value: string[]
  marked: boolean
  correct: boolean

  constructor(id, val, marked, correct) {
    this.questionId = id
    this.value = val
    this.marked = marked
    this.correct = correct
  }
}

export default class Exam {
  readonly id: number
  readonly name: string
  readonly body: string
  readonly timeLimit: number
  readonly passScore: number
  readonly averageRate: number
  readonly passRate: number
  readonly usersCount: number
  readonly passedCount: number
  readonly questionCount: number | null
  readonly hasImage: boolean
  readonly canRetest: boolean
  readonly description: string
  readonly checkResultOption: number
  readonly listIds: number[]
  public questions: Question[] = []
  public examTestQuestions: ExamTestQuestion[]
  readonly image: Image | undefined
  readonly difficultRate: number
  readonly price: number
  readonly purchasePrice: number
  readonly purchaseTax: number
  readonly targetUserId: number | null
  readonly examSkills: Skill[]
  readonly courses: Course[]
  readonly director: User
  readonly createdAt: Date
  readonly updatedAt: Date
  readonly examType: number
  readonly parentId: number | null
  readonly hideResult: boolean
  readonly isUseCoupon: boolean
  readonly useExamEvaluation: boolean
  readonly stripePublishableKey: string
  readonly examEvaluations: ExamEvaluation[] = []
  readonly autoDiagnosisEnabled: boolean
  readonly globalCss: string
  
  static RESULT_AFTER_SUBMIT = 1
  static RESULT_AFTER_QUESTION = 2

  static TYPE_NORMAL_EXAM = 1
  static TYPE_STOCK_EXAM = 2
  static TYPE_USE_RECOMENDATION_EXAM = 3
  static TYPE_USE_NIGATE_EXAM = 4
  static TYPE_USE_SPECIAL_EXAM = 5
  static TYPE_USE_EXAM_FROM_FAILED = 6

  userExams: UserExam[] = []

  readonly examImageUrl: string =
    'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQxS3lKPrf7JwvuNfssCIJEXv9ji1E_awmgqMCrE-P9cOLfhe-C'

  constructor(data: { [key: string]: any }) {
    this.id = data.id
    this.name = data.name
    this.body = data.body
    this.timeLimit = data.timeLimit
    this.passScore = data.passScore
    this.averageRate = data.averageRate
    this.passRate = data.passRate
    this.usersCount = data.usersCount
    this.passedCount = data.passedCount
    this.questionCount = data.questionCount ?? null
    this.hasImage = data.hasImage ?? true
    this.description = data.description
    this.createdAt = new Date(data.createdAt)
    this.updatedAt = new Date(data.updatedAt)
    this.checkResultOption = data.checkResultOption
    this.difficultRate = data.difficultRate
    this.price = data.price
    this.purchasePrice = data.purchasePrice
    this.purchaseTax = data.purchaseTax
    this.listIds = data.listIds
    this.canRetest = data.canRetest
    this.targetUserId = data.targetUserId
    this.examType = data.examType
    this.parentId = data.parentId
    this.hideResult = data.hideResult
    this.isUseCoupon = data.isUseCoupon
    this.stripePublishableKey = data.stripePublishableKey
    this.useExamEvaluation = data.useExamEvaluation
    this.autoDiagnosisEnabled = data.autoDiagnosisEnabled
    this.globalCss = data.globalCss

    if (data.userExams) {
      this.userExams = data.userExams.map(element => new UserExam(element))
    }
    if (data.qs) {
      this.questions = data.qs.map(element => new Question(element))
    }
    if (data.image) {
      this.image = new Image(data.image)
    }
    if (this.image) {
      this.examImageUrl = this.image.url
    }
    if (data.examSkills) {
      this.examSkills = data.examSkills.map(element => new Skill(element))
    }
    if (data.director) {
      this.director = new User(data.director)
    }
    if (data.courses) {
      this.courses = data.courses.map(item => new Course(item))
    }
    if (data.examEvaluations) {
      this.examEvaluations = data.examEvaluations.map(item => new ExamEvaluation(item))
    }
  }

  isStarted(userExam: UserExam | null) {
    return userExam && userExam.startedAt
  }

  isTestTimeOver(userExam: UserExam | null) {
    return (
      userExam &&
      userExam.startedAt &&
      differenceInSeconds(new Date(), userExam.startedAt) > this.timeLimit * 60
    )
  }

  isDone(userExam: UserExam | null) {
    return userExam && userExam.finishedAt
  }

  isPass(userExam: UserExam | null) {
    return userExam && userExam.score > this.passScore
  }

  get isRecommendationTest(){
    return !!this.targetUserId
  }

  get completedUserExams(){
    return this.userExams.filter(userExam => !!userExam.finishedAt && !userExam.isWaitingScoreCheck)
  }

  get userExam() {
    return this.userExams[0]
  }

  get historyUserExams() {
    return this.userExams.slice(1)
  }

  get isResultAfterSubmit(): boolean {
    return this.checkResultOption == Exam.RESULT_AFTER_SUBMIT
  }

  get isResultAfterQuestion(): boolean {
    return this.checkResultOption == Exam.RESULT_AFTER_QUESTION
  }

  get testUserExam() {
    return this.userExams.find(item => item.testMode)
  }

  get maxScore() {
    return this.questions.reduce((result, question) => result += question.maxScore, 0)
  }

  get questionsIgnoreDescription() {
    return this.userExam.questions.filter(q => !q.isDescriptionQuestion)
  }

  get isForUser(){
    return [Exam.TYPE_USE_RECOMENDATION_EXAM, Exam.TYPE_USE_NIGATE_EXAM, Exam.TYPE_USE_SPECIAL_EXAM].indexOf(this.examType) >= 0
  }

  get isShowEvaluation() {
    return (this.useExamEvaluation || this.autoDiagnosisEnabled) && this.examEvaluations.length > 0
  }

  answersFlowQuestion(userExam: UserExam): AnswerFlowQuestion[] {
    const answeredQuestionIds = uniq(userExam.answers.map(answer => answer.qId))
    const result = answeredQuestionIds.map(questionId => {
      const question = this.questions.find(question => question.id == questionId)
      if(question){
        let values = []
        if (question.isInlineSelect) {
          const scopeIds: number[] = uniq(question.answerItems.map(item => item.scopeId))
          const scopeAnswerItems = scopeIds.map(scopeId => question.answerItems.filter(item => item.scopeId == scopeId))
          values = scopeAnswerItems.map(answerItems => {
            for (let index = 0; index < answerItems.length; index++) {
              const answerItem = answerItems[index];
              const answer = userExam.answers.find(answer => answer.answerItemId == answerItem.id && answer.qId == questionId)
              if (answer) {
                return answer.body
              }
            }
            return ''
          })
        }
        // isTextareaの場合、結果は一つしかないので、以下に設定します。
        else if(question.isTextarea){
          const answer = userExam.answers.find(answer => answer.qId == questionId)
          values = [answer? answer.body : '']
        }
        else {
          values = question.answerItems.map(answerItem => {
            const answer = userExam.answers.find(answer => answer.answerItemId == answerItem.id && answer.qId == questionId)
            return answer ? answer.body : ''
          })
          // MulticheckboxまたはisCheckbox、isRadioの場合、アンサーに''を外すしないと、結果が正しくないです。
          if (question.isMultiCheckbox || question.isCheckbox || question.isRadio) {
            values = values.filter(item => item != '')
          }
        }

        return new AnswerFlowQuestion(questionId, values, true, true)
      }
    })
    return result
  }

  get groupQuestions(): GroupQuestion[] {
    const groups: GroupQuestion[] = []
    this.userExam.questions.forEach(question => {
      const groupId = question.group
      const group = groups.find(item => item.groupId === groupId)
      if (group) {
        group.questions.push(question)
      } else {
        groups.push(new GroupQuestion(groupId, [question]))
      }
    })

    return groups
  }

  getQuestionNumberById(questionId: number): number {
    const realGroupQuestions = this.groupQuestions.filter(item => !item.isDescriptionQuestion)
    for (let index = 0; index < realGroupQuestions.length; index++) {
      const groupQuestion = realGroupQuestions[index]
      if (groupQuestion.questions.some(item => item.id == questionId)) {
        return index + 1
      }
    }

    return 0
  }
}
